import React from 'react';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import classNames from 'classnames';
import styles from './ActivityIconButton.module.scss';

interface ActivityIconButtonProps {
  icon: React.ReactNode;
  count: number | string;
  type: 'approvals' | 'invites' | 'review' | 'signing' | 'out_for_signing';
  tooltip: string;
  onClick: () => void;
  isActive: boolean;
  disabled?: boolean;
}

const ActivityIconButton = React.forwardRef<HTMLButtonElement, ActivityIconButtonProps>(({
  icon,
  count,
  type,
  tooltip,
  onClick,
  isActive,
  disabled = false,
}, ref) => {
  const buttonId = `activity-button-${type}`;

  // Match tooltip background to icon background color
  const getTooltipColor = () => {
    switch (type) {
      case 'approvals':
        return '#ef5212'; // Secondary Orange
      case 'invites':
        return '#006c90'; // Primary Blue
      case 'review':
        return '#009688'; // Teal Accent
      case 'signing':
        return '#4caf50'; // Olive/Green
      case 'out_for_signing':
        return '#7b1fa2'; // Deep Purple
      default:
        return '#274156'; // Fallback dark blue
    }
  };

  return (
    <>
      <button
        ref={ref}
        id={buttonId}
        className={classNames(styles.activityButton, styles[`activityButton--${type}`], {
          [styles.activityButtonActive]: isActive,
          [styles.activityButtonDisabled]: disabled,
        })}
        onClick={onClick}
        disabled={disabled}
        aria-label={`${tooltip} (${count})`}
      >
        <span className={styles.iconWrapper}>{icon}</span>
        {count !== undefined && count !== null && count !== 0 && count !== '0' && Number(count) > 0 && (
          <span className={classNames(styles.badge, styles[`badge--${type}`])}>
            {typeof count === 'number' && count > 99 ? '99+' : count}
          </span>
        )}
      </button>
      <ReactTooltip
        anchorSelect={`#${buttonId}`}
        place="bottom"
        content={tooltip}
        className={styles[`tooltip--${type}`]}
        data-tooltip-type={type}
        style={{
          zIndex: 1000,
          fontSize: '12px',
          padding: '4px 8px',
          backgroundColor: getTooltipColor(),
          color: 'white',
        }}
      />
    </>
  );
});

ActivityIconButton.displayName = 'ActivityIconButton';

export default ActivityIconButton;
